import {
  BooleanInput,
  Labeled,
  SelectInput,
  TextInput,
  DateTimeInput,
} from 'ra-ui-materialui'
import React from 'react'
import { FormDataConsumer, useTranslate } from 'react-admin'
import { productStatusChoices } from 'services/cfg/form'
import { ProductName, ProductServiceName } from 'types/common'
import EventServiceBriefSection from 'ui-shared/FormComponents/EventServiceBriefSection'
import { EventFormDataConsumerProps } from '../GeneralTab'
import Kdivider from 'ui-shared/LayoutComponents/Kdivider'
import EventServiceSection from '../../EventsComponents/EventServiceSection'
import TimePicker from 'ui-shared/WrappedInputs/TimePicker'
import CustomizationInput from 'ui-shared/FormComponents/CustomizationInput'
import { PRODUCT_ENTITY_SERVICE_MAP } from 'services/utils/name-maps'
import { TailoredEventSelectionInput } from 'ui-shared/FormComponents/TailoredEventSelectionInput'
import useService from '../hooks/useService'
import useSelections from '../hooks/useSelections'
import KFormTab from 'ui-shared/WrappedInputs/KFormTab'
import { Badge } from '@mui/material'

type Props = {
  product: Extract<ProductName, 'rent' | 'music'>
}
type BaseProductService = Extract<
  ProductServiceName,
  'musicService' | 'rentService'
>

const BaseProductTab: React.FC<Props> = ({ product, ...props }) => {
  const entity = PRODUCT_ENTITY_SERVICE_MAP[product]
  const { hasSelections } = useSelections(`${product}s`)
  const { hasCustomization } = useService(`${product}Service`)
  const translate = useTranslate()

  return (
    <KFormTab
      {...props}
      label={
        <Badge
          badgeContent={hasSelections || hasCustomization ? 1 : 0}
          color="primary"
          variant="dot"
        >
          {translate(`form.tabs.${product}`)}
        </Badge>
      }
    >
      <SelectInput
        source={`${product}Status`}
        label="form.fields.status"
        choices={productStatusChoices}
        defaultValue={productStatusChoices[0].id}
      />

      <TextInput
        source={`${product}Notes`}
        multiline
        label="form.fields.notes"
      />

      <EventServiceSection source={`${product}Service` as ProductServiceName} />

      <TimePicker
        source={`${product}DeliveryTime`}
        label="form.fields.deliveryTime"
      />
      <DateTimeInput
        source={`${product}PickupDate`}
        label="form.fields.pickupDate"
        // find a way to restore the locales to format the date
        // locales={getGeoLang()}
      />

      {/* Base Product input */}
      <TailoredEventSelectionInput
        entity={entity}
        source={entity}
        label="form.fields.name"
      />

      <FormDataConsumer>
        {({ formData }: EventFormDataConsumerProps) => {
          const service =
            formData.productServices?.[
              `${product}Service` as BaseProductService
            ]

          return (
            <React.Fragment>
              <CustomizationInput
                source={`productServices.${product}Service.customization`}
                customization={service?.customization}
              />
              {service?.briefQuestions && service?.briefQuestions.length ? (
                <React.Fragment>
                  <Kdivider title="Event Brief" headingLevel="h3" />
                  <Labeled label="form.fields.briefConfirmation">
                    <BooleanInput
                      source={`productServices.${product}Service.briefConfirmed`}
                      label={service?.briefConfirmed ? 'Yes' : 'No'}
                      disabled
                    />
                  </Labeled>
                  <EventServiceBriefSection
                    serviceSource={`productServices.${product}Service`}
                  />
                </React.Fragment>
              ) : null}
            </React.Fragment>
          )
        }}
      </FormDataConsumer>
    </KFormTab>
  )
}

export default BaseProductTab
